import React, { useEffect, useState } from "react";
import {
    Grid,
    Typography,
    Button,
    TextField,
    CircularProgress
} from "@material-ui/core";

import { useStyles } from "./RefundDetails.styles";
import RefundTransactionService from "../../../services/RefundTransactionService";
import useCurrentFacility from "../../../hooks/useCurrentFacility";
import { useConfirmationDialog } from "../../../hooks/useConfirmationDialog";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import apiClient from "../../../auth/apiClient";

const refundTransactionService = new RefundTransactionService(apiClient);

const Label = React.memo(({ title, classes }) => {
    return (
        <Typography color="primary" className={(classes.centered, classes.labelTitle)}>
            {title}
        </Typography>
    );
});
Label.displayName = "Label";
const RefundDetails = ({ parkingTransactionId }) => {

    const classes = useStyles();
    const { facilityID: entityID } = useCurrentFacility();
    const { showDialog } = useConfirmationDialog();
    const enqueueSnackbar = useEnqueueSnackbar();

    const [refundDetails, setRefundDetails] = useState([]);
    const [refundReason, setRefundReason] = useState("");
    const [refundReasonError, setRefundReasonError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const getRefundDetails = async () => {
        setIsLoading(true);
        try {
            const response =
                await refundTransactionService.searchRefundTransactions({
                    entityID,
                    transactionID: parkingTransactionId,
                    limit: 1,
                    offset: 0,
                });

            if (response?.data?.collection) {
                setRefundDetails(response.data.collection);
            }
        } catch {
            enqueueSnackbar("Failed to fetch refund details", {
                variant: "error",
                tag: "FailedToFetchRefundDetails",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (parkingTransactionId)
            getRefundDetails();
    }, [parkingTransactionId]);

    const refundConfirmation = async (ticketNumber) => {
        if (!refundReason.trim()) {
            setRefundReasonError("Required");
            return;
        }

        const response = await showDialog({
            title: "Refund Confirmation",
            message: `Are you sure you want to refund payment on ticket ${ticketNumber}.`,
            buttons: [
                { name: "Yes", color: "primary" },
                { name: "No", color: "secondary" },
            ],
        });

        if (response === "No") return;

        enqueueSnackbar("ToDo: Implement refund functionality", {
            variant: "info",
        });
    };

    return (
        <Grid
            container
            direction="column"
            className={classes.refundContainer}
        >
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    {refundDetails.map((transaction) => (
                        <Grid
                            container
                            direction="column"
                            spacing={3}
                            key={transaction?.ticketNumber}
                        >
                            <Grid item >
                                <Grid item>
                                    <Typography variant="h5" className={classes.title}>
                                        Refund Details
                                    </Typography>
                                </Grid>

                                <Grid item>
                                    <Label title="Ticket" classes={classes} />
                                    <Typography className={classes.value}>{transaction?.ticketNumber}</Typography>
                                </Grid>

                                <Grid item>
                                    <Label title="Customer" classes={classes} />
                                    <Typography className={classes.value}>
                                        {transaction?.customerFirstName}{" "}
                                        {transaction?.customerLastName}
                                    </Typography>
                                </Grid>

                                <Grid item>
                                    <Label title="Amount" classes={classes} />
                                    <Typography className={classes.value}>
                                        ${transaction?.amount?.toFixed(2) ?? "0.00"}
                                    </Typography>
                                </Grid>

                                <Grid item>
                                    <TextField
                                        variant="outlined"
                                        required
                                        margin="normal"
                                        fullWidth
                                        value={refundReason}
                                        label="Refund Reason"
                                        onChange={(e) => {
                                            setRefundReason(e.target.value);
                                            if (e.target.value.trim()) {
                                                setRefundReasonError("");
                                            }
                                        }}
                                        error={!!refundReasonError}
                                        helperText={refundReasonError}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.submitButton}
                                    onClick={() =>
                                        refundConfirmation(transaction?.ticketNumber)
                                    }
                                >
                                    SUBMIT REFUND
                                </Button>
                            </Grid>
                        </Grid>
                    ))}
                </>
            )}
        </Grid>
    );
};

export default RefundDetails;